import { counterpartApiConnector } from 'apiConnector/counterpart/counterpart';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import { ofType } from 'redux-observable';
import { EMPTY, of } from 'rxjs';
import { bufferTime, catchError, mergeMap } from 'rxjs/operators';
import type { Action } from 'state/actions';
import type { ProductChange } from 'state/secondaryMarket/productSearch';
import {
  secondaryMarketProductsValueChanged,
  secondaryMarketProductsValueChangedDone,
  secondaryMarketProductsValueChangedFailed,
} from 'state/secondaryMarket/productSearch';
import type { EpicWithThunk } from 'state/state.common';
import type { ApiFunctionalError } from 'utils/http/apiErrorHelper';
import type { HttpError } from 'utils/http/httpClient.model';

function processChange(c: ProductChange, res: ISalesCounterpart[]): Action[] {
  const result = res.find(s => s.eliotCode?.toUpperCase() === c.value?.toUpperCase());
  if (result === undefined) {
    return [secondaryMarketProductsValueChangedFailed([{ field: c.field, rowId: c.rowId }], 'Eliot code not found')];
  }
  return [
    secondaryMarketProductsValueChangedDone([{ field: c.field, rowId: c.rowId, hasBeenUpdated: true }]),
    secondaryMarketProductsValueChanged({ ...c, value: result }),
  ];
}

export function searchCustodianOrClientProductEpic(aggregate: number): EpicWithThunk {
  return (actions$, _state$, { httpClient, scheduler }) => {
    const { fetchSalesCounterpartsFromEliotCodes } = counterpartApiConnector(httpClient);
    return actions$.pipe(
      ofType('SECONDARY_MARKET_PRODUCTS_CUSTODIAN_OR_CLIENT_CHANGED_REQUEST'),
      bufferTime(aggregate, scheduler),
      mergeMap(actions => {
        if (actions.length === 0) {
          return EMPTY;
        } else {
          return of(actions);
        }
      }),
      mergeMap(actions => {
        const changes = actions.flatMap(c => c.change);
        return fetchSalesCounterpartsFromEliotCodes(actions.flatMap(c => c.change.value)).pipe(
          mergeMap(res => {
            return changes.flatMap(c => processChange(c, res));
          }),
          catchError((e: HttpError<ApiFunctionalError>) => {
            return of(
              secondaryMarketProductsValueChangedFailed(
                changes.map(c => ({ field: c.field, rowId: c.rowId })),
                e.payload?.message,
              ),
            );
          }),
        );
      }),
    );
  };
}
